import { Badge } from "@/components/ui/badge";
import { Check, X, AlertCircle } from "lucide-react";

interface PayloadReadOnlyViewProps {
  /** Optional JSON schema to enrich field labels and resolve enum titles. */
  schema?: Record<string, any> | null;
  /** Configured values for this payload. */
  values: Record<string, any>;
  /** Optional schema-fetch error to surface a hint. */
  schemaError?: unknown;
}

interface FieldEntry {
  key: string;
  label: string;
  description?: string;
  value: unknown;
  enumLabels?: Record<string, string>;
}

function fieldsFromSchema(
  schema: Record<string, any> | null | undefined,
  values: Record<string, any>
): FieldEntry[] {
  if (!schema || schema.type !== "object" || !schema.properties) {
    // No schema – fall back to the raw value keys, in insertion order.
    return Object.keys(values).map((key) => ({
      key,
      label: key,
      value: values[key],
    }));
  }
  const props = schema.properties as Record<string, any>;
  const orderedKeys = [
    ...Object.keys(props),
    // Include any extra value keys that aren't in the schema.
    ...Object.keys(values).filter((k) => !(k in props)),
  ];
  return orderedKeys.map((key) => {
    const def = props[key] || {};
    const enumValues: any[] | undefined = def.enum;
    const enumNames: string[] | undefined = def["x-enumNames"] || def.enumNames;
    const enumLabels =
      enumValues && enumNames
        ? Object.fromEntries(enumValues.map((v, i) => [String(v), enumNames[i] ?? String(v)]))
        : undefined;
    return {
      key,
      label: def.title || key,
      description: def.description,
      value: values[key],
      enumLabels,
    };
  });
}

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === "";
}

function ValueRenderer({
  value,
  enumLabels,
  label,
}: {
  value: unknown;
  enumLabels?: Record<string, string>;
  label: string;
}) {
  if (isEmpty(value)) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (typeof value === "boolean") {
    return value ? (
      <Badge
        variant="outline"
        className="gap-1 border-success/40 bg-success-muted text-success"
        aria-label={`${label} is enabled`}
      >
        <Check className="h-3 w-3" aria-hidden />
        Enabled
      </Badge>
    ) : (
      <Badge
        variant="outline"
        className="gap-1 text-muted-foreground"
        aria-label={`${label} is disabled`}
      >
        <X className="h-3 w-3" aria-hidden />
        Disabled
      </Badge>
    );
  }
  if (typeof value === "number") {
    return <span className="font-mono text-sm">{value}</span>;
  }
  if (typeof value === "string") {
    const display = enumLabels?.[value] ?? value;
    if (enumLabels) {
      return <Badge variant="secondary">{display}</Badge>;
    }
    if (display.length > 60 || display.includes("\n")) {
      return (
        <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-muted/40 rounded px-2 py-1.5 max-w-full">
          {display}
        </pre>
      );
    }
    return <span className="text-sm">{display}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground">—</span>;
    if (value.every((v) => typeof v === "string" || typeof v === "number")) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => (
            <Badge key={i} variant="outline" className="text-xs font-normal">
              {String(v)}
            </Badge>
          ))}
        </div>
      );
    }
    return (
      <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-muted/40 rounded px-2 py-1.5 max-w-full">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  if (typeof value === "object") {
    return (
      <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-muted/40 rounded px-2 py-1.5 max-w-full">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return <span className="text-sm">{String(value)}</span>;
}

export function PayloadReadOnlyView({
  schema,
  values,
  schemaError,
}: PayloadReadOnlyViewProps) {
  const fields = fieldsFromSchema(schema, values).filter(
    // Hide schema-defined fields that have no configured value to keep
    // the read-only view focused on what's actually set.
    (f) => !isEmpty(f.value) || f.key in values
  );

  if (fields.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        No settings configured
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {schemaError && !schema && (
        <div
          className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2"
          role="status"
        >
          <AlertCircle className="h-3 w-3" aria-hidden />
          <span>Schema unavailable — showing raw values</span>
        </div>
      )}
      <dl className="rounded-md border bg-muted/20 divide-y divide-border">
        {fields.map((f) => (
          <div
            key={f.key}
            className="grid grid-cols-[minmax(140px,200px)_1fr] gap-3 px-3 py-2 text-sm items-start"
          >
            <dt className="font-medium text-muted-foreground">
              {f.label}
              {f.description && (
                <span className="block text-xs font-normal text-muted-foreground/70 mt-0.5">
                  {f.description}
                </span>
              )}
            </dt>
            <dd className="min-w-0">
              <ValueRenderer
                value={f.value}
                enumLabels={f.enumLabels}
                label={f.label}
              />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
